import { lazy } from 'react'

const RELOAD_KEY = 'vite_chunk_reload'
const RELOAD_COOLDOWN_MS = 10_000

/** Cache partagé pour éviter des import() parallèles sur la même route au navigate. */
const moduleLoadPromises = new Map()

function isChunkLoadError(error) {
  const message = error?.message || ''
  return (
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Importing a module script failed') ||
    message.includes('error loading dynamically imported module') ||
    error?.name === 'ChunkLoadError'
  )
}

function canReload() {
  const lastReload = sessionStorage.getItem(RELOAD_KEY)
  if (!lastReload) return true
  return Date.now() - Number(lastReload) > RELOAD_COOLDOWN_MS
}

function reloadOnce() {
  if (!canReload()) return
  sessionStorage.setItem(RELOAD_KEY, String(Date.now()))
  window.location.reload()
}

function isInvalidModuleError(error) {
  const message = error?.message || ''
  return message.includes("Cannot read properties of undefined (reading 'default')")
}

async function retryImport(importFn, retries, baseDelay) {
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const mod = await importFn()
      if (!mod || typeof mod.default === 'undefined') {
        throw new Error("Cannot read properties of undefined (reading 'default')")
      }
      return mod
    } catch (error) {
      if (attempt === retries || (!isChunkLoadError(error) && !isInvalidModuleError(error))) {
        throw error
      }
      await new Promise((r) => setTimeout(r, baseDelay * 2 ** attempt))
    }
  }
}

function loadModule(importFn, { retries = 2, baseDelay = 1000 } = {}) {
  if (!moduleLoadPromises.has(importFn)) {
    moduleLoadPromises.set(
      importFn,
      retryImport(importFn, retries, baseDelay).catch((error) => {
        moduleLoadPromises.delete(importFn)
        throw error
      })
    )
  }
  return moduleLoadPromises.get(importFn)
}

export function resetLazyModuleCache() {
  moduleLoadPromises.clear()
}

/**
 * Charge impérativement un module dynamique avec la même robustesse que `lazyWithRetry`
 * (retries + reload sur chunk périmé), mais renvoie le module résolu au lieu d'un composant lazy.
 *
 * Utile pour précharger ET résoudre un composant afin de le rendre *synchronement* ensuite
 * (sans passer par Suspense), évitant le flash du fallback au premier montage.
 */
export function preloadWithRetry(importFn, { retries = 2, baseDelay = 1000 } = {}) {
  return loadModule(importFn, { retries, baseDelay }).catch((error) => {
    if (isChunkLoadError(error) || isInvalidModuleError(error)) {
      reloadOnce()
      return new Promise(() => {})
    }
    throw error
  })
}

/**
 * Drop-in replacement for React.lazy() that retries failed dynamic imports
 * with exponential backoff before falling back to a full page reload.
 *
 * Handles stale chunk errors after deployments (old hash → 404).
 */
export function lazyWithRetry(importFn, { retries = 2, baseDelay = 1000 } = {}) {
  return lazy(() =>
    loadModule(importFn, { retries, baseDelay }).catch((error) => {
      if (isChunkLoadError(error) || isInvalidModuleError(error)) {
        reloadOnce()
        return new Promise(() => {})
      }
      throw error
    })
  )
}

/**
 * Global handler for Vite's native preload errors.
 * Call once in main.jsx — acts as a safety net for preload failures
 * that happen outside of React.lazy() (e.g. CSS preloads, transitive deps).
 */
export function registerPreloadErrorHandler() {
  window.addEventListener('vite:preloadError', (event) => {
    event.preventDefault()
    reloadOnce()
  })
}
